/**
 * Authed file download from Oxbo-P.
 *
 * The xlsx report endpoints (`…/arch-report.xlsx`, `…/afe-seguimiento.xlsx`)
 * stream a binary attachment, not JSON — so they don't fit the RTK Query
 * baseQuery (which parses JSON). Instead we fetch them directly with the SAME
 * bearer token pApi attaches (prod-login token, or the dev-minted token), read
 * the body as a Blob, and trigger a browser "Save as" via a transient object
 * URL. The server's Content-Disposition filename is honoured when present,
 * otherwise the caller-supplied fallback is used.
 *
 * Mirrors pApi's base URL + auth exactly so it hits the same host under the
 * same org scope. Never assumes a token — a token-less (mocks-only) build still
 * fetches, letting MSW / the demo identity answer.
 */
import { getProdToken } from "./prodAuth";
import { getDevToken } from "./devAuth";

const P_BASE_URL = import.meta.env.VITE_OXBO_P_URL ?? "https://p.dev.oxbo.energy";

/** The same bearer token pApi attaches (prod-login first, else dev-minted). */
function authToken(): string | null {
  return getProdToken() ?? getDevToken();
}

/** Parse the filename out of a Content-Disposition header, if any. */
function filenameFromDisposition(header: string | null): string | null {
  if (!header) return null;
  // RFC 5987 `filename*=UTF-8''name` first, then a plain `filename="name"`.
  const star = /filename\*=(?:UTF-8'')?([^;]+)/i.exec(header);
  if (star?.[1]) {
    try {
      return decodeURIComponent(star[1].replace(/^"|"$/g, ""));
    } catch {
      /* fall through to the plain form */
    }
  }
  const plain = /filename="?([^";]+)"?/i.exec(header);
  return plain?.[1] ?? null;
}

/** Trigger a browser download of `blob` as `filename` via a transient <a>. */
function saveBlob(blob: Blob, filename: string): void {
  const url = URL.createObjectURL(blob);
  try {
    const a = document.createElement("a");
    a.href = url;
    a.download = filename;
    a.rel = "noopener";
    document.body.appendChild(a);
    a.click();
    a.remove();
  } finally {
    // Revoke on the next tick so the click has started the download.
    setTimeout(() => URL.revokeObjectURL(url), 0);
  }
}

/**
 * GET a `/v1`-relative path from Oxbo-P with the bearer token and save the
 * response body as a file. `path` must start with `/` (e.g.
 * `/daily-reports/{id}/arch-report.xlsx`). Throws on a non-ok response so the
 * caller can surface an error.
 */
export async function downloadPFile(path: string, fallbackName: string): Promise<void> {
  const token = authToken();
  const res = await fetch(`${P_BASE_URL}/v1${path}`, {
    method: "GET",
    headers: token ? { Authorization: `Bearer ${token}` } : {},
  });
  if (!res.ok) {
    let detail = `${res.status} ${res.statusText}`;
    try {
      const j = (await res.clone().json()) as { detail?: unknown };
      if (j && typeof j.detail === "string" && j.detail) detail = j.detail;
    } catch {
      /* non-JSON error body */
    }
    throw new Error(detail);
  }
  const blob = await res.blob();
  const name = filenameFromDisposition(res.headers.get("Content-Disposition")) ?? fallbackName;
  saveBlob(blob, name);
}
